import { addresses, bidIncrements as bidIncrementsTable } from '~/db/schema';
import { getAuctionHouseDetailsBySlug, getAuctionHouseListWithoutPagination } from '~/services/api/auction-houses';
import { AuctionType, CategoryIdToName, CloudflareStreamDetails, StreamDetails } from '~/types';
import { clsx, type ClassValue } from 'clsx';
import { differenceInMilliseconds } from 'date-fns';
import ky, { HTTPError, Options as KyOptions } from 'ky';
import { parse } from 'superjson';
import { twMerge } from 'tailwind-merge';
import { z } from 'zod';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const formatPrice = (price: number, currency?: string) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currencyDisplay: 'narrowSymbol',
    currency: currency ?? 'MYR',
  }).format(price);

export function formatDate(dateString: string | number | null | Date | undefined) {
  if (!dateString) return '';

  const options: Intl.DateTimeFormatOptions = {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  };

  const formattedDate = new Date(dateString).toLocaleDateString('en-GB', options);
  return formattedDate;
}

export function formatDateTime(dateString: string | number | Date | null | undefined) {
  if (dateString === null || dateString === undefined) return '';

  const options: Intl.DateTimeFormatOptions = {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    hour12: true,
  };

  const formattedDate = new Date(dateString).toLocaleString('en-US', options);

  const parts = formattedDate.split(' ');
  if (parts.length > 1 && !parts[0].endsWith(',')) {
    parts[0] += ',';
  }

  return parts.join(' ');
}

export const formatNumber = (quantity: number): string => {
  return new Intl.NumberFormat('en-US').format(quantity);
};

export const formatCurrency = (value: number): string => {
  return `MYR ${value.toLocaleString('en-MY', { minimumFractionDigits: 0 })}`;
};

export const formatToK = (value: number): string => {
  return `MYR ${value / 1000}k`;
};

export const capitalizeFirstLetter = (string: string) => {
  return string.charAt(0).toUpperCase() + string.slice(1);
};

export const getInitials = (name: string) => {
  if (name?.split(' ').length > 1) {
    return name
      .split(' ')
      .map((name) => name.charAt(0))
      .join('');
  } else {
    return name?.charAt(0);
  }
};

export const calcAuctionHouseRatingFromAuctionReviews = (
  reviews:
    | NonNullable<Awaited<ReturnType<typeof getAuctionHouseDetailsBySlug>>>['auctions'][number]['reviews']
    | NonNullable<
        Awaited<ReturnType<typeof getAuctionHouseListWithoutPagination>>
      >[number]['auctions'][number]['reviews'],
) => {
  const totalSumOfRatings = reviews.reduce((acc, curr) => acc + curr.rating, 0);
  const totalNumberOfRatings = reviews.length;

  const averageRating = (totalNumberOfRatings / totalSumOfRatings).toFixed(1);

  return Number(averageRating);
};

export function getFormattedDistanceToNow(date: Date | number): string {
  const distance = differenceInMilliseconds(date, new Date());

  const oneDayInMilliseconds = 24 * 60 * 60 * 1000;
  if (distance >= oneDayInMilliseconds) {
    const days = Math.floor(distance / oneDayInMilliseconds);
    return `${days} day${days > 1 ? 's' : ''}`;
  }

  const hours = Math.floor((distance / (1000 * 60 * 60)) % 24);
  const minutes = Math.floor((distance / (1000 * 60)) % 60);
  const seconds = Math.floor((distance / 1000) % 60);

  // Pad hours, minutes, and seconds with leading zeros if needed
  const paddedHours = String(hours).padStart(2, '0');
  const paddedMinutes = String(minutes).padStart(2, '0');
  const paddedSeconds = String(seconds).padStart(2, '0');

  return `${paddedHours}h ${paddedMinutes}m ${paddedSeconds}s`;
}

export function slugify(string: string, options?: any): string {
  if (typeof string !== 'string') {
    throw new Error('slugify: string argument expected');
  }

  options = typeof options === 'string' ? { replacement: options } : options || {};

  const replacement = options.replacement === undefined ? '-' : options.replacement;

  let slug = string
    .normalize()
    .split('')
    // replace characters based on charMap
    .reduce(function (result, ch) {
      let appendChar = locales[ch] || charMap[ch] || ch;
      if (appendChar === replacement) {
        appendChar = ' ';
      }
      return result + appendChar;
    }, '');

  slug = slug
    .replace(/[^A-Za-z0-9\s]/g, '')
    .trim()
    .replace(/\s+/g, replacement)
    .toLowerCase();

  return slug;
}

const charMap = JSON.parse(
  '{"$":"dollar","%":"percent","&":"and","<":"less",">":"greater","|":"or","¢":"cent","£":"pound","¤":"currency","¥":"yen","©":"(c)","ª":"a","®":"(r)","º":"o","À":"A","Á":"A","Â":"A","Ã":"A","Ä":"A","Å":"A","Æ":"AE","Ç":"C","È":"E","É":"E","Ê":"E","Ë":"E","Ì":"I","Í":"I","Î":"I","Ï":"I","Ð":"D","Ñ":"N","Ò":"O","Ó":"O","Ô":"O","Õ":"O","Ö":"O","Ø":"O","Ù":"U","Ú":"U","Û":"U","Ü":"U","Ý":"Y","Þ":"TH","ß":"ss","à":"a","á":"a","â":"a","ã":"a","ä":"a","å":"a","æ":"ae","ç":"c","è":"e","é":"e","ê":"e","ë":"e","ì":"i","í":"i","î":"i","ï":"i","ð":"d","ñ":"n","ò":"o","ó":"o","ô":"o","õ":"o","ö":"o","ø":"o","ù":"u","ú":"u","û":"u","ü":"u","ý":"y","þ":"th","ÿ":"y","Ā":"A","ā":"a","Ă":"A","ă":"a","Ą":"A","ą":"a","Ć":"C","ć":"c","Č":"C","č":"c","Ď":"D","ď":"d","Đ":"DJ","đ":"dj","Ē":"E","ē":"e","Ė":"E","ė":"e","Ę":"e","ę":"e","Ě":"E","ě":"e","Ğ":"G","ğ":"g","Ģ":"G","ģ":"g","Ĩ":"I","ĩ":"i","Ī":"i","ī":"i","Į":"I","į":"i","İ":"I","ı":"i","Ķ":"k","ķ":"k","Ļ":"L","ļ":"l","Ľ":"L","ľ":"l","Ł":"L","ł":"l","Ń":"N","ń":"n","Ņ":"N","ņ":"n","Ň":"N","ň":"n","Ō":"O","ō":"o","Ő":"O","ő":"o","Œ":"OE","œ":"oe","Ŕ":"R","ŕ":"r","Ř":"R","ř":"r","Ś":"S","ś":"s","Ş":"S","ş":"s","Š":"S","š":"s","Ţ":"T","ţ":"t","Ť":"T","ť":"t","Ũ":"U","ũ":"u","Ū":"u","ū":"u","Ů":"U","ů":"u","Ű":"U","ű":"u","Ų":"U","ų":"u","Ŵ":"W","ŵ":"w","Ŷ":"Y","ŷ":"y","Ÿ":"Y","Ź":"Z","ź":"z","Ż":"Z","ż":"z","Ž":"Z","ž":"z","Ə":"E","ƒ":"f","Ơ":"O","ơ":"o","Ư":"U","ư":"u","ǈ":"LJ","ǉ":"lj","ǋ":"NJ","ǌ":"nj","Ș":"S","ș":"s","Ț":"T","ț":"t","ə":"e","˚":"o","Ά":"A","Έ":"E","Ή":"H","Ί":"I","Ό":"O","Ύ":"Y","Ώ":"W","ΐ":"i","Α":"A","Β":"B","Γ":"G","Δ":"D","Ε":"E","Ζ":"Z","Η":"H","Θ":"8","Ι":"I","Κ":"K","Λ":"L","Μ":"M","Ν":"N","Ξ":"3","Ο":"O","Π":"P","Ρ":"R","Σ":"S","Τ":"T","Υ":"Y","Φ":"F","Χ":"X","Ψ":"PS","Ω":"W","Ϊ":"I","Ϋ":"Y","ά":"a","έ":"e","ή":"h","ί":"i","ΰ":"y","α":"a","β":"b","γ":"g","δ":"d","ε":"e","ζ":"z","η":"h","θ":"8","ι":"i","κ":"k","λ":"l","μ":"m","ν":"n","ξ":"3","ο":"o","π":"p","ρ":"r","ς":"s","σ":"s","τ":"t","υ":"y","φ":"f","χ":"x","ψ":"ps","ω":"w","ϊ":"i","ϋ":"y","ό":"o","ύ":"y","ώ":"w","Ё":"Yo","Ђ":"DJ","Є":"Ye","І":"I","Ї":"Yi","Ј":"J","Љ":"LJ","Њ":"NJ","Ћ":"C","Џ":"DZ","А":"A","Б":"B","В":"V","Г":"G","Д":"D","Е":"E","Ж":"Zh","З":"Z","И":"I","Й":"J","К":"K","Л":"L","М":"M","Н":"N","О":"O","П":"P","Р":"R","С":"S","Т":"T","У":"U","Ф":"F","Х":"H","Ц":"C","Ч":"Ch","Ш":"Sh","Щ":"Sh","Ъ":"U","Ы":"Y","Ь":"","Э":"E","Ю":"Yu","Я":"Ya","а":"a","б":"b","в":"v","г":"g","д":"d","е":"e","ж":"zh","з":"z","и":"i","й":"j","к":"k","л":"l","м":"m","н":"n","о":"o","п":"p","р":"r","с":"s","т":"t","у":"u","ф":"f","х":"h","ц":"c","ч":"ch","ш":"sh","щ":"sh","ъ":"u","ы":"y","ь":"","э":"e","ю":"yu","я":"ya","ё":"yo","ђ":"dj","є":"ye","і":"i","ї":"yi","ј":"j","љ":"lj","њ":"nj","ћ":"c","ѝ":"u","џ":"dz","Ґ":"G","ґ":"g","Ғ":"GH","ғ":"gh","Қ":"KH","қ":"kh","Ң":"NG","ң":"ng","Ү":"UE","ү":"ue","Ұ":"U","ұ":"u","Һ":"H","һ":"h","Ә":"AE","ә":"ae","Ө":"OE","ө":"oe","Ա":"A","Բ":"B","Գ":"G","Դ":"D","Ե":"E","Զ":"Z","Է":"E\'","Ը":"Y\'","Թ":"T\'","Ժ":"JH","Ի":"I","Լ":"L","Խ":"X","Ծ":"C\'","Կ":"K","Հ":"H","Ձ":"D\'","Ղ":"GH","Ճ":"TW","Մ":"M","Յ":"Y","Ն":"N","Շ":"SH","Չ":"CH","Պ":"P","Ջ":"J","Ռ":"R\'","Ս":"S","Վ":"V","Տ":"T","Ր":"R","Ց":"C","Փ":"P\'","Ք":"Q\'","Օ":"O\'\'","Ֆ":"F","և":"EV","ء":"a","آ":"aa","أ":"a","ؤ":"u","إ":"i","ئ":"e","ا":"a","ب":"b","ة":"h","ت":"t","ث":"th","ج":"j","ح":"h","خ":"kh","د":"d","ذ":"th","ر":"r","ز":"z","س":"s","ش":"sh","ص":"s","ض":"dh","ط":"t","ظ":"z","ع":"a","غ":"gh","ف":"f","ق":"q","ك":"k","ل":"l","م":"m","ن":"n","ه":"h","و":"w","ى":"a","ي":"y","ً":"an","ٌ":"on","ٍ":"en","َ":"a","ُ":"u","ِ":"e","ْ":"","٠":"0","١":"1","٢":"2","٣":"3","٤":"4","٥":"5","٦":"6","٧":"7","٨":"8","٩":"9","پ":"p","چ":"ch","ژ":"zh","ک":"k","گ":"g","ی":"y","۰":"0","۱":"1","۲":"2","۳":"3","۴":"4","۵":"5","۶":"6","۷":"7","۸":"8","۹":"9","฿":"baht","ა":"a","ბ":"b","გ":"g","დ":"d","ე":"e","ვ":"v","ზ":"z","თ":"t","ი":"i","კ":"k","ლ":"l","მ":"m","ნ":"n","ო":"o","პ":"p","ჟ":"zh","რ":"r","ს":"s","ტ":"t","უ":"u","ფ":"f","ქ":"k","ღ":"gh","ყ":"q","შ":"sh","ჩ":"ch","ც":"ts","ძ":"dz","წ":"ts","ჭ":"ch","ხ":"kh","ჯ":"j","ჰ":"h","Ṣ":"S","ṣ":"s","Ẁ":"W","ẁ":"w","Ẃ":"W","ẃ":"w","Ẅ":"W","ẅ":"w","ẞ":"SS","Ạ":"A","ạ":"a","Ả":"A","ả":"a","Ấ":"A","ấ":"a","Ầ":"A","ầ":"a","Ẩ":"A","ẩ":"a","Ẫ":"A","ẫ":"a","Ậ":"A","ậ":"a","Ắ":"A","ắ":"a","Ằ":"A","ằ":"a","Ẳ":"A","ẳ":"a","Ẵ":"A","ẵ":"a","Ặ":"A","ặ":"a","Ẹ":"E","ẹ":"e","Ẻ":"E","ẻ":"e","Ẽ":"E","ẽ":"e","Ế":"E","ế":"e","Ề":"E","ề":"e","Ể":"E","ể":"e","Ễ":"E","ễ":"e","Ệ":"E","ệ":"e","Ỉ":"I","ỉ":"i","Ị":"I","ị":"i","Ọ":"O","ọ":"o","Ỏ":"O","ỏ":"o","Ố":"O","ố":"o","Ồ":"O","ồ":"o","Ổ":"O","ổ":"o","Ỗ":"O","ỗ":"o","Ộ":"O","ộ":"o","Ớ":"O","ớ":"o","Ờ":"O","ờ":"o","Ở":"O","ở":"o","Ỡ":"O","ỡ":"o","Ợ":"O","ợ":"o","Ụ":"U","ụ":"u","Ủ":"U","ủ":"u","Ứ":"U","ứ":"u","Ừ":"U","ừ":"u","Ử":"U","ử":"u","Ữ":"U","ữ":"u","Ự":"U","ự":"u","Ỳ":"Y","ỳ":"y","Ỵ":"Y","ỵ":"y","Ỷ":"Y","ỷ":"y","Ỹ":"Y","ỹ":"y","–":"-","‘":"\'","’":"\'","“":"\\"","”":"\\"","„":"\\"","†":"+","•":"*","…":"...","₠":"ecu","₢":"cruzeiro","₣":"french franc","₤":"lira","₥":"mill","₦":"naira","₧":"peseta","₨":"rupee","₩":"won","₪":"new shequel","₫":"dong","€":"euro","₭":"kip","₮":"tugrik","₯":"drachma","₰":"penny","₱":"peso","₲":"guarani","₳":"austral","₴":"hryvnia","₵":"cedi","₸":"kazakhstani tenge","₹":"indian rupee","₺":"turkish lira","₽":"russian ruble","₿":"bitcoin","℠":"sm","™":"tm","∂":"d","∆":"delta","∑":"sum","∞":"infinity","♥":"love","元":"yuan","円":"yen","﷼":"rial","ﻵ":"laa","ﻷ":"laa","ﻹ":"lai","ﻻ":"la"}',
);
const locales = JSON.parse(
  '{"bg":{"Й":"Y","Ц":"Ts","Щ":"Sht","Ъ":"A","Ь":"Y","й":"y","ц":"ts","щ":"sht","ъ":"a","ь":"y"},"de":{"Ä":"AE","ä":"ae","Ö":"OE","ö":"oe","Ü":"UE","ü":"ue","ß":"ss","%":"prozent","&":"und","|":"oder","∑":"summe","∞":"unendlich","♥":"liebe"},"es":{"%":"por ciento","&":"y","<":"menor que",">":"mayor que","|":"o","¢":"centavos","£":"libras","¤":"moneda","₣":"francos","∑":"suma","∞":"infinito","♥":"amor"},"fr":{"%":"pourcent","&":"et","<":"plus petit",">":"plus grand","|":"ou","¢":"centime","£":"livre","¤":"devise","₣":"franc","∑":"somme","∞":"infini","♥":"amour"},"pt":{"%":"porcento","&":"e","<":"menor",">":"maior","|":"ou","¢":"centavo","∑":"soma","£":"libra","∞":"infinito","♥":"amor"},"uk":{"И":"Y","и":"y","Й":"Y","й":"y","Ц":"Ts","ц":"ts","Х":"Kh","х":"kh","Щ":"Shch","щ":"shch","Г":"H","г":"h"},"vi":{"Đ":"D","đ":"d"},"da":{"Ø":"OE","ø":"oe","Å":"AA","å":"aa","%":"procent","&":"og","|":"eller","$":"dollar","<":"mindre end",">":"større end"},"nb":{"&":"og","Å":"AA","Æ":"AE","Ø":"OE","å":"aa","æ":"ae","ø":"oe"},"it":{"&":"e"},"nl":{"&":"en"},"sv":{"&":"och","Å":"AA","Ä":"AE","Ö":"OE","å":"aa","ä":"ae","ö":"oe"}}',
);

export const getCategoryName = (categoryId: string): string => {
  return CategoryIdToName[categoryId as keyof typeof CategoryIdToName] || categoryId;
};

export const getAuctionType = (auctionType: string): string => {
  switch (auctionType) {
    case AuctionType.TIME2LIVE:
      return 'Time2Live';
    case AuctionType.TIMEONLINE:
      return 'TimeOnline';
    case AuctionType.LIVEONLY:
      return 'Live Auction';
    default:
      return '-';
  }
};

export const numberSerializer = (value: unknown) => {
  if (typeof value === 'string') {
    return Number(value);
  }
  return value;
};

export const generateRandomColorName = () => {
  const colors = [
    'rose',
    'pink',
    'purple',
    'fuchsia',
    'violet',
    'indigo',
    'blue',
    'cyan',
    'teal',
    'emerald',
    'green',
    'lime',
    'yellow',
    'amber',
    'orange',
    'red',
  ];

  const randomIndex = Math.floor(Math.random() * colors.length);
  return colors[randomIndex];
};

export function generateInvoiceNumber(lastInvoiceNumber: number) {
  lastInvoiceNumber++;

  if (lastInvoiceNumber <= 9999) {
    return `INV-${lastInvoiceNumber.toString().padStart(4, '0')}`;
  } else {
    return `INV-${lastInvoiceNumber}`;
  }
}

export function extractInvoiceNumber(invoiceNumber: string) {
  // Use a regular expression to match digits after any non-digit characters
  const match = invoiceNumber.match(/\D+(\d+)/);

  // If there's a match, convert the captured group to a number
  if (match && match[1]) {
    return parseInt(match[1], 10);
  }

  // Return null or some default value if no number is found
  return null;
}

export async function* uint8ArrayToAsyncIterable(uint8Array: Uint8Array): AsyncIterable<Uint8Array> {
  yield uint8Array;
}

export const checkFileSize = (fileSize: number, smallerThanSizeMB: number) => {
  const validFileSize = fileSize / 1024 / 1024 < smallerThanSizeMB;
  return validFileSize;
};

export function formatFileSize(fileSizeInBytes: number) {
  // Convert bytes to more readable format
  if (fileSizeInBytes < 1024) {
    return fileSizeInBytes + ' bytes';
  } else if (fileSizeInBytes < 1048576) {
    return (fileSizeInBytes / 1024).toFixed(2) + ' KB';
  } else {
    return (fileSizeInBytes / 1048576).toFixed(2) + ' MB';
  }
}

export function findLastMatching<T>(array: Array<T>, condition: (item: T) => boolean) {
  for (let i = array.length - 1; i >= 0; i--) {
    if (condition(array[i])) {
      return array[i];
    }
  }
  return undefined;
}

export function generateRandomBidderId() {
  return Math.floor(1000 + Math.random() * 9000).toString();
}

export function handleZodError(err: unknown, callback?: (message?: string) => void) {
  if (err instanceof z.ZodError) {
    console.error('Validation failed:', err.errors);
    callback?.(err.errors[0].message);
  } else {
    console.error('Validation failed');
    callback?.('An unknown error occurred');
  }
}

export function zodAlwaysRefine<T extends z.ZodTypeAny>(zodType: T) {
  return z.any().superRefine(async (value, ctx) => {
    const res = await zodType.safeParseAsync(value);

    if (res.success === false)
      for (const issue of res.error.issues) {
        ctx.addIssue(issue);
      }
  }) as unknown as T;
}

export const removeNonNumeric = (input: string) => {
  return input.replace(/[^0-9]/g, '');
};

export const getEffectiveBidIncrement = (
  bidIncrements: (typeof bidIncrementsTable.$inferSelect)[],
  bidAmount: number,
) => {
  return (
    bidIncrements.find(
      (increment) => bidAmount >= increment.priceFrom && (increment.priceTo === null || bidAmount <= increment.priceTo),
    )?.increments ?? bidIncrements[bidIncrements.length - 1].increments
  );
};

export const formatAddress = (address?: typeof addresses.$inferSelect) => {
  if (!address) return '';

  const { line1, line2, state, postcode, city, country } = address;
  return `${line1} ${line2 ? ' ' + line2 : ''} ${city} ${postcode} ${state} ${country}`;
};

type GroupKey = string | number | symbol;

export function groupBy<T extends Record<K, GroupKey>, K extends keyof T>(arr: T[], key: K): Record<GroupKey, T[]> {
  return arr.reduce(
    (acc, obj) => {
      const groupKey = obj[key];
      if (!acc[groupKey]) {
        acc[groupKey] = [];
      }
      acc[groupKey].push(obj);
      return acc;
    },
    {} as Record<GroupKey, T[]>,
  );
}

export function normalizeStreamDetails(details: StreamDetails): CloudflareStreamDetails | null {
  if (!details) return null;
  if ('livestreamUid' in details) {
    return {
      uid: details.livestreamUid,
      webRTC: {
        url: details.webRTCUrl || '',
      },
    };
  } else {
    return details;
  }
}

export async function apiWrapper<T>(url: string, options?: KyOptions): Promise<T> {
  try {
    const response = await ky.get(url, options);
    return parse(await response.text()) as T;
  } catch (error: unknown) {
    if (error instanceof HTTPError) {
      if (error.response.status === 503) {
        throw new Error('Server is currently overloaded. Please try again later.');
      }
      // You can add more specific error handling here if needed
      throw new Error(`HTTP error ${error.response.status}: ${error.response.statusText}`);
    }
    // If it's not an HTTPError, re-throw the original error
    throw error;
  }
}
